// src/Admin.js
import React, { useState, useEffect } from 'react';
import { fetchContexts, clearContextScopedCaches, getContextId, triggerBmImport, STORAGE_KEYS } from './v2Api';

function TripSelector({ user }) {
  const [loading, setLoading] = useState(true);
  const [contexts, setContexts] = useState([]);
  const [error, setError] = useState(null);
  const [activeContextId, setActiveContextId] = useState(getContextId);
  const [staleSelfHealed, setStaleSelfHealed] = useState(false);
  const [loadTrigger, setLoadTrigger] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchContexts(user)
      .then(data => {
        if (cancelled) return;
        const ctxList = data.contexts || [];

        // Stale self-heal: if the stored contextId is not in the returned list
        // (deleted custom context, or from another session), reset to default so
        // the feed isn't silently 404-ing on every request.
        const storedId = localStorage.getItem(STORAGE_KEYS.devContextId);
        if (storedId && !ctxList.some(c => c.id === storedId)) {
          localStorage.removeItem(STORAGE_KEYS.devContextId);
          localStorage.removeItem(STORAGE_KEYS.devContextName);
          setActiveContextId(getContextId());
          setStaleSelfHealed(true);
        } else if (storedId) {
          const activeCtx = ctxList.find(c => c.id === storedId);
          if (activeCtx) localStorage.setItem(STORAGE_KEYS.devContextName, activeCtx.name || activeCtx.id);
        }

        setContexts(ctxList);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.message || 'Failed to load trips');
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [user, loadTrigger]);

  const handleSelect = (contextId, contextName) => {
    localStorage.setItem(STORAGE_KEYS.devContextId, contextId);
    localStorage.setItem(STORAGE_KEYS.devContextName, contextName || contextId);
    clearContextScopedCaches();
    window.location.reload();
  };

  if (loading) {
    return <p data-testid="trip-selector-loading">Loading trips…</p>;
  }

  if (error) {
    return (
      <div data-testid="trip-selector-error">
        <p>Error loading trips: {error}</p>
        <button data-testid="trip-selector-retry" onClick={() => setLoadTrigger(t => t + 1)}>
          Retry
        </button>
      </div>
    );
  }

  if (contexts.length === 0) {
    return <p data-testid="trip-selector-empty">No trips available.</p>;
  }

  return (
    <div data-testid="trip-selector">
      {staleSelfHealed && (
        <p data-testid="stale-context-notice">Previous trip was not found; reset to default.</p>
      )}
      <p>Active: <strong data-testid="active-context-label">{activeContextId}</strong></p>
      <ul data-testid="trip-list">
        {contexts.map(ctx => (
          <li key={ctx.id}>
            <button
              data-testid={`trip-option-${ctx.id}`}
              onClick={() => handleSelect(ctx.id, ctx.name || ctx.id)}
              aria-current={ctx.id === activeContextId ? 'true' : undefined}
            >
              {ctx.name || ctx.id}{ctx.id === activeContextId ? ' ✓' : ''}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

const Admin = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (user && !user.isAnonymous) {
      user.getIdTokenResult().then(result => {
        setIsAdmin(result.claims.admin === true);
      }).catch(() => setIsAdmin(false));
    } else {
      setIsAdmin(false);
    }
  }, [user]);

  const updateDB = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await triggerBmImport(user);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h1>Admin Page</h1>

      {user && !user.isAnonymous && (
        <section data-testid="trip-selector-section">
          <h2>Developer: Trip Selector</h2>
          <TripSelector user={user} />
        </section>
      )}

      {isAdmin && (
        <section>
          <h2>Database</h2>
          <button onClick={updateDB} disabled={loading}>
            {loading ? 'Updating...' : 'Update DB'}
          </button>
          {error && <p style={{ color: 'red' }}>Error: {error}</p>}
          {data && (
            <div>
              <h2>Events</h2>
              <p>New: {data.events.new}</p>
              <p>Updated: {data.events.updated}</p>
              <p>Deleted: {data.events.deleted}</p>

              <h2>Camps</h2>
              <p>New: {data.camps.new}</p>
              <p>Updated: {data.camps.updated}</p>
              <p>Deleted: {data.camps.deleted}</p>

              <h2>Art</h2>
              <p>New: {data.art.new}</p>
              <p>Updated: {data.art.updated}</p>
              <p>Deleted: {data.art.deleted}</p>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default Admin;
